import { useLayoutEffect, useRef, useState } from 'react';
import { Alert } from 'antd';
import { useBreakpoint, useOnline } from '@/shared/lib/hooks';

/** CSS-переменная с высотой плашки на телефоне: липкая шапка владельца сдвигается под неё
 *  (`top: var(--offline-banner-h, 0px)`), иначе плашка закрывала бы кнопки шапки. */
const BANNER_HEIGHT_VAR = '--offline-banner-h';

/** Mounted once near the app root — shows while `navigator.onLine` is false. */
export function OfflineBanner() {
  const online = useOnline();
  const { isMobile } = useBreakpoint();

  if (online) return null;
  if (isMobile) return <MobileOfflineBanner />;

  return (
    <Alert
      banner
      type="warning"
      showIcon
      message="Нет подключения к интернету"
      style={{ position: 'sticky', top: 0, zIndex: 1000 }}
    />
  );
}

/** Телефон: то же предупреждение, но с отступами под вырез/«остров» iPhone и боковые вырезы в
 *  ландшафте. Плашка прижата к верху экрана (`fixed`) и при любой прокрутке остаётся видимой, а
 *  место под неё в потоке держит распорка той же высоты; высота публикуется в `--offline-banner-h`
 *  — липкая шапка владельца встаёт под плашку, а не под неё. */
function MobileOfflineBanner() {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const root = document.documentElement;
    const publish = () => {
      setHeight(el.offsetHeight);
      root.style.setProperty(BANNER_HEIGHT_VAR, `${el.offsetHeight}px`);
    };
    publish();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(publish);
    observer?.observe(el);
    return () => {
      observer?.disconnect();
      root.style.removeProperty(BANNER_HEIGHT_VAR);
    };
  }, []);

  return (
    <div style={{ height }}>
      <div ref={ref} style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000 }}>
        <Alert
          banner
          type="warning"
          showIcon
          message="Нет подключения к интернету"
          style={{
            paddingTop: 'calc(8px + env(safe-area-inset-top, 0px))',
            paddingLeft: 'max(12px, env(safe-area-inset-left, 0px))',
            paddingRight: 'max(12px, env(safe-area-inset-right, 0px))',
          }}
        />
      </div>
    </div>
  );
}
